<template>
  <div id="onlineOrderingStyle" :class="{ mediaOnly: !isSuperAdmin }">
    <div class="container" v-if="branch">

      <div class="page-title">
        <div class="left">
          <span>{{ $t('kiosk_style.page_title') }}</span>
          <label>{{ $t('kiosk_style.page_subtitle') }}</label>
        </div>
        <button class="btn btn-primary" @click="save">{{ $t('save') }}</button>
      </div>

      <div class="openWebApp" v-if="isSuperAdmin">
        <div class="grow"></div>
        <div class="btn btn-secondary" style="margin-right: 10px;" @click="exportConfig">Exporter</div>
        <input id="importFile" type="file" style="display:none;" accept="application/json" @change="importConfig($event)">
        <label for="importFile" class="btn btn-secondary" style="margin-right: 10px;">Importer...</label>
        <div class="btn btn-secondary" @click="reset">Réinitialiser le visuel</div>
      </div>

      <Tabs>
        <TabSection ref="generalPanel" title="Général" v-if="isSuperAdmin">
          <Panel title="Configuration des couleurs" collapsible>
            <p class="description">
              Création des couleurs disponibles pour la configuration du visuel.
              <button class="btn btn-primary add" @click="addColor">
                <svgicon src="/icon/add.svg" height="15" width="15"></svgicon>
              </button>
            </p>
            <div class="color" v-for="(color, colorIndex) of colors" :key="colorIndex">
              <div class="group">
                <label>Nom</label>
                <input type="text" v-model="color.name" placeholder="Nom de la couleur">
              </div>
              <div class="group">
                <label>Couleur</label>
                <input type="color" v-model="color.value">
              </div>
              <a class="delete" href="javascript:void(0)" @click="colors.splice(colorIndex, 1)">
                <svgicon src="/icon/close.svg" height="15" width="15"></svgicon>
              </a>
            </div>
          </Panel>

          <Panel title="Style avancé" collapsible>
            <p class="description">Vous pouvez ajouter ici du style CSS personnalisé qui sera ajouté à votre application.
            </p>
            <div class="group">
              <label>CSS</label>
              <textarea class="customCss" placeholder="" v-model="customCss"></textarea>
            </div>
          </Panel>

          <Panel title="Configuration avancée" collapsible>
            <div class="columns">
              <div class="column">
                <div class="group">
                  <input type="checkbox" id="chk_hideCategoryImages" v-model="hideCategoryImages">
                  <label for="chk_hideCategoryImages">Cacher les images de catégories</label>
                </div>
                <div class="group">
                  <input type="checkbox" id="chk_hideOutOfStock" v-model="hideOutOfStockProducts">
                  <label for="chk_hideOutOfStock">Cacher les produits en rupture de stock</label>
                </div>
              </div>
            </div>
          </Panel>
        </TabSection>

        <TabSection class="mediaPanel" ref="mediaPanel" :title="$t('kiosk_style.media_title')" v-if="company && ready">

          <label class="panel__description">{{ $t('kiosk_style.media_subtitle') }}</label>

          <div class="layouts-guide">
            <div class="layout">
              <label class="layout__title">{{ $t('kiosk_style.vertical') }}</label>
              <img class="layout__image" src="@/assets/img/kiosk/vertical.png">
            </div>
            <div class="layout">
              <label class="layout__title">{{ $t('kiosk_style.horizontal') }}</label>
              <img class="layout__image" src="@/assets/img/kiosk/horizontal.png">
            </div>
            <div class="layout">
              <label class="layout__title">{{ $t('kiosk_style.airxtouch') }}</label>
              <img class="layout__image" src="@/assets/img/kiosk/airxtouch.png">
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="image-upload__header">
                <div class="image__number">1</div>
                <div class="image__description">
                  <div class="description__top">{{ $t('kiosk_style.standby_title') }}</div>
                  <div class="description__bottom">{{ $t('kiosk_style.standby_description') }}</div>
                </div>
              </div>
              <ImageUpload identifier="uploadImageKioskStandbyImage" :previewHeight="100"
                tooltip="kiosk_style.standby_tooltip" :object="company.kioskStandbyImage" :maxSizeInMb="2"
                @changed="company.kioskStandbyImage = $event"></ImageUpload>
            </div>
            <div class="column">
              <div class="image-upload__header">
                <div class="image__number">2</div>
                <div class="image__description">
                  <div class="description__top">{{ $t('kiosk_style.logo_title') }}</div>
                  <div class="description__bottom">{{ $t('kiosk_style.logo_description') }}</div>
                </div>
              </div>
              <ImageUpload identifier="uploadImageKioskLogo" :previewHeight="100" tooltip="kiosk_style.logo_tooltip"
                :object="company.kioskLogo" :maxSizeInMb="0.8" @changed="company.kioskLogo = $event"></ImageUpload>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="image-upload__header">
                <div class="image__number">3</div>
                <div class="image__description">
                  <div class="description__top">{{ $t('kiosk_style.banner_title') }}</div>
                  <div class="description__bottom">{{ $t('kiosk_style.banner_description') }}</div>
                </div>
              </div>
              <ImageUpload identifier="uploadImageKioskBannerImage" :previewHeight="100"
                tooltip="kiosk_style.banner_tooltip" :object="company.kioskBannerImage" :maxSizeInMb="1"
                @changed="company.kioskBannerImage = $event"></ImageUpload>
            </div>
            <div class="column">
              <div class="image-upload__header">
                <div class="image__number">4</div>
                <div class="image__description">
                  <div class="description__top">{{ $t('kiosk_style.promo_title') }}</div>
                  <div class="description__bottom">{{ $t('kiosk_style.promo_description') }}</div>
                </div>
              </div>
              <ImageUpload identifier="uploadImageKioskPromoImage" :previewHeight="100"
                tooltip="kiosk_style.promo_tooltip" :object="company.kioskPromoImage" :maxSizeInMb="0.8"
                @changed="company.kioskPromoImage = $event"></ImageUpload>
            </div>
          </div>

          <div class="columns" v-if="isSuperAdmin">
            <div class="column">
              <div class="image-upload__header">
                <div class="image__number" style="opacity: 0;"></div>
                <div class="image__description">
                  <div class="description__top">{{ $t('kiosk_style.background_title') }}</div>
                  <div class="description__bottom">{{ $t('kiosk_style.background_description') }}</div>
                </div>
              </div>
              <ImageUpload identifier="uploadImageKioskBackgroundImage" :previewHeight="100"
                tooltip="kiosk_style.background_tooltip" :object="company.kioskBackgroundImage" :maxSizeInMb="1"
                @changed="company.kioskBackgroundImage = $event"></ImageUpload>
            </div>
            <div class="column">&nbsp;</div>
          </div>

          <div class="slideshow" :title="$t('branding.banner_slideshow')">
            <div class="image-upload__header">
              <div class="image__number">5</div>
              <div class="image__description">
                <div class="description__top">{{ $t('kiosk_style.slideshow_title') }}</div>
                <div class="description__bottom">{{ $t('kiosk_style.slideshow_description') }}</div>
              </div>
            </div>
            <div class="slideshow__images">
              <div class="slideshow__image" v-for="(image, imageKey) of slideshowImages()" :key="'img-' + imageKey">
                <ImageUpload :identifier="'slideshowImage' + image.key" tooltip="kiosk_style.banner_tooltip"
                  :object="image" @changed="slideshowImageChanged(image.key, $event)" :previewHeight="100"
                  :maxSizeInMb="1"></ImageUpload>
              </div>
              <div class="slideshow__image slideshow--add" @click="addSlideshowImage">
                <svgicon src="icon/add-circle.svg" :height="16" :width="16"></svgicon>
                {{ $t('kiosk_style.add_banner') }}
              </div>
            </div>
          </div>
        </TabSection>

        <template v-for="(style, styleIndex) of configurableStyle" v-if="selectors && isSuperAdmin">
          <TabSection :ref="'stylePanel' + styleIndex" :title="$t(style.description)">

            <div class="group" v-for="(selector, selectorIndex) of style.selectors"
              :key="styleIndex + '-' + selectorIndex">

              <label>{{ $t(selector.description) }}</label>

              <div class="selector" v-if="hasAttribute(selector, 'color')">
                <Tooltip text="Choix d'une couleur de texte"></Tooltip>
                <ColorSelector :colors="colors" :value="getSelector(selector.selector, 'color').value"
                  v-if="getSelector(selector.selector, 'color')"
                  @changed="getSelector(selector.selector, 'color').value = $event"
                  placeholder="Choisir une couleur de texte"></ColorSelector>
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'fill')">
                <Tooltip text="Choix d'une couleur d'icône"></Tooltip>
                <ColorSelector :colors="colors" :value="getSelector(selector.selector, 'fill').value"
                  v-if="getSelector(selector.selector, 'fill')"
                  @changed="getSelector(selector.selector, 'fill').value = $event"
                  placeholder="Choisir une couleur d'icône"></ColorSelector>
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'border-radius')">
                <Tooltip text="Choix d'une rondeur de bordure (en radius px)"></Tooltip>
                <input type="number" min="0" step="1" class="number-select"
                  v-if="getSelector(selector.selector, 'border-radius')"
                  v-model="getSelector(selector.selector, 'border-radius').value"
                  placeholder="Rondeur des bordures (radius px)">
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'background-color')">
                <Tooltip text="Choix d'une couleur de fond"></Tooltip>
                <ColorSelector :colors="colors" :value="getSelector(selector.selector, 'background-color').value"
                  v-if="getSelector(selector.selector, 'background-color')"
                  @changed="getSelector(selector.selector, 'background-color').value = $event"
                  placeholder="Choisir une couleur de fond"></ColorSelector>
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'background-image')">
                <!-- TODO -->
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'opacity')">
                <Tooltip text="Choix d'une valeur de transparence (entre 0 et 1)"></Tooltip>
                <input type="number" min="0" max="1" step="0.01" class="number-select"
                  v-if="getSelector(selector.selector, 'opacity')"
                  v-model="getSelector(selector.selector, 'opacity').value" placeholder="Transparence">
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'font-family')">
                <Tooltip text="Choix d'une police d'écriture"></Tooltip>
                <select class="font-select" v-if="getSelector(selector.selector, 'font-family')"
                  :style="{ fontFamily: getSelector(selector.selector, 'font-family').value }"
                  v-model="getSelector(selector.selector, 'font-family').value">
                  <option value="" selected hidden>Choisir une police d'écriture...</option>
                  <option v-for="font of fonts" :value="font" :style="{ fontFamily: font }" :key="font">{{ font }}
                  </option>
                </select>
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'font-size')">
                <Tooltip text="Taille de la police (en px)"></Tooltip>
                <input type="number" min="0" step="1" class="number-select"
                  v-if="getSelector(selector.selector, 'font-size')"
                  v-model="getSelector(selector.selector, 'font-size').value" placeholder="Taille de police">
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'font-weight')">
                <Tooltip text="Choix d'une épaisseur de police"></Tooltip>
                <select class="font-select" v-if="getSelector(selector.selector, 'font-weight')"
                  :style="{ fontWeight: getSelector(selector.selector, 'font-weight').value, fontFamily: 'Roboto' }"
                  v-model="getSelector(selector.selector, 'font-weight').value">
                  <option value="" selected hidden>Choisir une épaisseur d'écriture...</option>
                  <option v-for="weight of weights" :value="weight" :style="{ fontWeight: weight }" :key="weight">
                    {{ weightText(weight) }}</option>
                </select>
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'display')">
                <Tooltip text="Choix de méthode d'affichage"></Tooltip>
                <select class="font-select" v-if="getSelector(selector.selector, 'display')"
                  v-model="getSelector(selector.selector, 'display').value">
                  <option value="" selected hidden>Choisir une méthode d'affichage...</option>
                  <option v-for="mode of display" :value="mode.value" :key="mode.name">{{ mode.name }}</option>
                </select>
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'border-color')">
                <Tooltip text="Choix d'une couleur de bordure"></Tooltip>
                <ColorSelector :colors="colors" :value="getSelector(selector.selector, 'border-color').value"
                  v-if="getSelector(selector.selector, 'border-color')"
                  @changed="getSelector(selector.selector, 'border-color').value = $event"
                  placeholder="Choisir une couleur de bordure"></ColorSelector>
              </div>

              <div class="selector" v-if="hasRegexAttribute(selector, /^border(-(left|right|top|bottom))?$/)">
                <Tooltip text="Choix une taille, une couleur et un style de bordure"></Tooltip>
                <BorderSelector :colors="colors"
                  :value="getSelector(selector.selector, hasRegexAttribute(selector, /^border(-(left|right|top|bottom))?$/)).value"
                  v-if="getSelector(selector.selector, hasRegexAttribute(selector, /^border(-(left|right|top|bottom))?$/))"
                  @changed="getSelector(selector.selector, hasRegexAttribute(selector, /^border(-(left|right|top|bottom))?$/)).value = $event">
                </BorderSelector>
              </div>

              <div class="selector" v-if="hasAttribute(selector, 'placeholder')">
                <Tooltip text="Choix d'une couleur de texte de remplacement"></Tooltip>
                <ColorSelector :colors="colors" :value="getSelector(selector.selector, 'placeholder').value"
                  v-if="getSelector(selector.selector, 'placeholder')"
                  @changed="getSelector(selector.selector, 'placeholder').value = $event"
                  placeholder="Choisir une couleur de texte de remplacement"></ColorSelector>
              </div>

              <div class="selector background" v-if="hasAttribute(selector, 'background')">
                <Tooltip
                  text="Choix d'un fond personnalisé: <br>linear-gradient(couleur1, couleur2)<br>rgba(255,255,255,255)<br>etc.">
                </Tooltip>
                <input type="text" class="background-select" v-if="getSelector(selector.selector, 'background')"
                  v-model="getSelector(selector.selector, 'background').value"
                  placeholder="Fond personnalisé: linear-gradient(couleur1, couleur2), rgba(255,255,255,255), #000000, etc. ">
                <div class="preview" :style="{ background: getSelector(selector.selector, 'background').value }"></div>
              </div>

            </div>
          </TabSection>
        </template>
      </Tabs>

      <button class="btn btn-save btn-primary" @click="save" v-if="isSuperAdmin">{{ $t('save') }}</button>

      <div class="footer-actions" v-if="!isSuperAdmin">
        <button class="btn btn-primary" @click="save">{{ $t('save') }}</button>
      </div>

    </div>
  </div>
</template>

<script>
  import ImageUpload from '@/components/tools/ImageUpload.vue';
  import ColorSelector from "@/components/style/ColorSelector.vue";
  import BorderSelector from "@/components/style/BorderSelector.vue";
  import Tooltip from "@/components/tools/Tooltip";
  import { mapActions } from "vuex";
  import ConfigurableStyle from "@/resources/configurableStyle.json";

  export default {
    name: "onlineOrderingStyle",

    components: { ColorSelector, Tooltip, ImageUpload, BorderSelector },

    data() {
      return {
        companyId: null,
        company: null,
        branchId: null,
        version: "0.0.1",
        ready: false,
        customCss: "",
        configurableStyle: ConfigurableStyle.kiosk,
        colors: [],
        selectors: [],
        hideOutOfStockProducts: false,
        hideCategoryImages: false,
        fonts: ["Roboto", "Segoe UI", "Segoe UI Black", "Oswald", "Cinzel", "Acme", "Abel",
          "Aldrich", "Blogger Sans", "Cinzel Decorative", "Lato", "Yanone Kaffeesatz",
          "Arial", "Times New Roman", "Helvetica", "Courier New", "Dharma Gothic",
          "Verdana", "Georgia", "Palatino", "Garamond", "Vanguard CF", "GT America Condensed",
          "Trebuchet MS", "Arial Black", "Impact", "Nanami", "Quicksand", "Happiness",
          "Chivo", "Distro", "HVD", "Gagalin", "Raleway", "Raleway Black", "Josefin Slab", "Josefin Sans", "Barlow"].sort(),
        weights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
        display: [{ name: "Block", value: "block" }, { name: "Hidden", value: "none" }],
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "style.kiosk";
      this.$store.state.pageIcon = "icon/art.svg";

      this.branchId = this.$route.query.branch;
      this.companyId = this.$route.query.company;
      this.company = await this.$store.getters.getContextCompany;

      if (this.company) {
        this.getCompanyStyle();
        this.buildSelectors();

        if (!this.company.images) {
          this.company.images = {};
        }
      }
      this.ready = true;
    },

    computed: {
      branch() {
        return this.$store.state.contextBranch;
      },
      isSuperAdmin() {
        return this.$store.getters.loggedUserHasSuperAdmin;
      },
      weightText() {
        let weightArray = ["Thin", "Extra-Light", "Light", "Normal", "Medium", "Semi-Bold", "Bold", "Extra-Bold", "Black"]
        return weight => {
          return weightArray[Math.floor(weight / 100) - 1];
        };
      }
    },

    watch: {
      "company": function (to, from) {
        if (to && !from) {
          this.getCompanyStyle();
          this.buildSelectors();
        }
      }
    },

    methods: {
      ...mapActions(["saveCompany"]),

      slideshowImages() {
        if (!this.company.images) {
          return [];
        }
        let keys = Object.keys(this.company.images);
        let images = [];
        for (let key of keys) {
          if (this.company.images[key].tags && this.company.images[key].tags.indexOf("slideshow") > -1) {
            this.company.images[key].key = key;
            images.push(this.company.images[key]);
          }
        }
        images.sort((first, second) => { return first.priority > second.priority });
        return images;
      },

      addSlideshowImage() {
        let uuid = generateUUID();
        this.company.images[uuid] = {
          tags: ["slideshow"],
          image: { url: "" }
        };
        this.$forceUpdate();
      },

      getCompanyStyle() {
        let css = this.company.kioskCssData;
        if (css && this.isSuperAdmin) {
          this.colors = css.colors || [];
          this.customCss = css.customCss || "";
          this.version = css.version || "0.0.1";
          this.selectors = css.selectors || [];
          this.hideOutOfStockProducts = css.hideOutOfStockProducts || false;
          this.hideCategoryImages = css.hideCategoryImages || false;
        }
        if (!this.company.images) {
          this.company.images = {};
        }
        this.company.kioskBackgroundImage = this.company.images.kioskBackgroundImage || { url: "" };
        this.company.kioskLogo = this.company.images.kioskLogo || { url: "" };
        this.company.kioskBannerImage = this.company.images.kioskBannerImage || { url: "" };
        this.company.kioskStandbyImage = this.company.images.kioskStandbyImage || { url: "" };
        this.company.kioskPromoImage = this.company.images.kioskPromoImage || { url: "" };
      },

      reset() {
        if (!this.isSuperAdmin) {
          return;
        }
        this.version = "0.0.1";
        this.selectors = [];
        this.customCss = "";
        this.colors = [];
        this.hideCategoryImages = false;
        this.hideOutOfStockProducts = false;
        this.buildSelectors();
      },

      buildSelectors() {
        if (!this.isSuperAdmin) {
          return;
        }
        let selectors = this.configurableStyle.map(x => {
          if (x.selectors.length > 0) {
            return x.selectors;
          }
        }).filter(x => typeof x !== "undefined");
        selectors = this.flatten(selectors);

        let builtSelectors = [];

        for (let item of selectors) {
          for (let modifiable of item.modifiable) {
            let found = this.selectors.filter((x) => {
              return x.selector && x.selector.sort().join(',') === item.selector.sort().join(',') && x.attribute === modifiable;
            });
            builtSelectors.push({
              selector: item.selector,
              attribute: modifiable,
              value: found.length > 0 ? found[0].value : ""
            });
          }
        }

        this.selectors = builtSelectors;
      },

      getSelector(selector, attribute) {
        let found = this.selectors.filter((x) => x.selector === selector && x.attribute === attribute);
        return found.length > 0 ? found[0] : null;
      },

      flatten(array) {
        return array.reduce((flat, next) => flat.concat(next), []);
      },

      getCssData() {
        return {
          version: this.version,
          colors: this.colors,
          customCss: this.customCss,
          selectors: this.selectors,
          hideCategoryImages: this.hideCategoryImages,
          hideOutOfStockProducts: this.hideOutOfStockProducts
        }
      },

      async save() {
        showSpinner();
        let data = {
          branchId: this.branchId,
          companyId: this.companyId,
          data: {
            images: {
              kioskBackgroundImage: this._getImageData(this.company.kioskBackgroundImage),
              kioskBannerImage: this._getImageData(this.company.kioskBannerImage),
              kioskLogo: this._getImageData(this.company.kioskLogo),
              kioskPromoImage: this._getImageData(this.company.kioskPromoImage),
              kioskStandbyImage: this._getImageData(this.company.kioskStandbyImage),
            },
            kioskCssData: this.isSuperAdmin ? this.getCssData() : undefined
          }
        };
        data.data.images = this.formatSlideshowImages(data.data.images);
        let response = await this.saveCompany(data);
        if (response) {
          toast(this.$t("company.success"), "success");
          this.company.images = response.images;
          if (this.isSuperAdmin) {
            this.company.kioskCssData = response.kioskCssData;
          }
          this.getCompanyStyle();
          this.buildSelectors();
        }
        hideSpinner();
      },

      formatSlideshowImages(data) {
        let images = JSON.parse(JSON.stringify(data));

        //Append slideshow images
        for (let key of Object.keys(this.company.images)) {
          if (typeof this.company.images[key] !== "object" || !this.company.images[key].tags || this.company.images[key].tags.indexOf("slideshow") === -1) { //Ignore images that are not slideshow images
            continue;
          }
          images[key] = JSON.parse(JSON.stringify(this.company.images[key]));
        }

        // Parse image data
        for (let key of Object.keys(images)) {
          if (!images[key] || !images[key].tags || images[key].tags.indexOf("slideshow") === -1) { //Ignore images that are not slideshow images
            continue;
          }
          let imageData;
          if (images[key].image.url === "" && !images[key].image.deleted) {
            imageData = null;
          } else {
            imageData = getImageData(images[key].image);
          }
          if (imageData === null) { // null img is to be deleted
            images[key] = null;
          } else {
            images[key] = {
              image: imageData,
              tags: images[key].tags
            }
          }
        }

        // Set image priority
        let index = 1;
        for (let key of Object.keys(images)) {
          if (!images[key] || !images[key].tags || images[key].tags.indexOf("slideshow") === -1) { //Ignore images that are not slideshow images
            continue;
          }
          images[key].priority = index;
          index++;
        }

        return images;
      },

      slideshowImageChanged(key, image) {
        this.company.images[key].image = image;
        if (image.deleted) {
          this.company.images[key].url = "";
        }
      },

      addColor() {
        this.colors.push({
          name: "",
          value: "#ffffff"
        })
      },

      _getImageData(image) {
        let data = getImageData(image);
        if (data) {
          return {
            image: data
          }
        } else {
          return data;
        }
      },

      hasAttribute(selector, attribute) {
        return (selector.modifiable.indexOf(attribute) > -1);
      },

      exportConfig() {
        let exportDate = new Date().toISOString().slice(0, 16).replace("T", "_");
        window._exportAsJson("css-data-export_" + this.company.nameCanonical + "_" + exportDate, this.getCssData());
      },

      async importConfig(e) {
        console.log("event", e);
        let file = e.target.files[0];

        console.log("file type", file.type);

        if (!["application/json"].includes(file.type)) {
          toast("Format de fichier invalide. Veuillez choisir un .json.", "error");
        } else if (file.size > window.MAX_FILE_SIZE) {
          this.$modal.show("file-size-error");
        } else {
          let fileReader = new FileReader();
          fileReader.onload = async () => {
            let data = JSON.parse(fileReader.result.toString());
            console.log("data", data);
            this.company.kioskCssData = data;
            this.getCompanyStyle();
            this.buildSelectors();
            this.forceUpdateAll();
          };
          fileReader.readAsText(file);
        }
      },

      forceUpdateAll() {
        this.$refs.generalPanel.$forceUpdate();
        this.$refs.mediaPanel.$forceUpdate();
        for (let i = 0; i < this.configurableStyle.length; i++) {
          this.$refs["stylePanel" + i][0].$forceUpdate();
        }
        this.$forceUpdate();
      },

      hasRegexAttribute(selector, regex) {
        for (let modifiable of selector.modifiable) {
          if (regex.test(modifiable)) {
            return modifiable;
          }
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  .panel__description {
    margin-bottom: 20px;
  }

  .page-title {
    label {
      font-weight: normal;
      margin-top: 6px;
      display: block;
    }
  }

  .layouts-guide {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding: 0 30px 10px 30px;
    border-bottom: 1px dashed #e6e6e6;
    margin-bottom: 20px;

    .layout {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 10px 20px;
    }
  }

  .mediaPanel {
    .imageUpload {
      padding-left: 40px;
    }

    .columns {
      padding-bottom: 20px;
    }
  }

  .image-upload__header {
    display: flex;
    align-items: center;
    padding-bottom: 12px;

    .image__number {
      border: 2px solid #e6e6e6;
      border-radius: 100%;
      font-size: 12px;
      color: #fc6650;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 25px;
      width: 25px;
      min-height: 25px;
      min-width: 25px;
      margin-right: 15px;
      font-weight: 900;
    }

    .image__description {
      font-size: 14px;

      .description__top {
        color: #fc6650;
        font-weight: bold;
        padding-bottom: 2px;
      }

      .description__bottom {
        color: #808080;
      }
    }
  }

  .slideshow {
    border-top: 1px dashed #e6e6e6;
    padding-top: 20px;

    .title {
      display: flex;
      justify-content: space-between;
      text-align: center;

      .left {
        text-align: left;
      }

      .text {
        font-weight: bold;
        font-size: 18px;
      }
    }

    .slideshow__images {
      margin: 0 -10px 0 10px;
      display: flex;
      flex-wrap: wrap;
    }

    .slideshow__image {
      width: 100%;
      max-width: calc(50% - 64px);
      margin: 10px 32px;

      .imageUpload {
        padding-left: 0;
      }
    }

    @media screen and (max-width: 720px) {
      .slideshow__images {
        flex-direction: column;
        margin: 0;

        .slideshow__image {
          max-width: initial;
          width: auto;
          margin: 10px 32px;
        }
      }
    }

    .slideshow--add {
      border: 1px dashed #e6e6e6;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      color: #fc6650;
      min-height: 180px;
      cursor: pointer;
      font-size: 14px;

      .svgicon {
        margin-right: 15px;
        fill: #fc6650 !important;
      }
    }
  }

  .selector {
    display: flex;
    align-items: center;

    .tooltip {
      margin-right: 10px;
    }

    .color-select-holder,
    .number-select,
    .font-select,
    .background-select {
      flex-grow: 1;
    }
  }

  input[type=text].file-upload {
    margin-bottom: 0;
  }

  .btnDelete {
    margin-left: 10px;
    display: flex;
    fill: white;
  }

  .background {
    position: relative;

    .preview {
      position: absolute;
      right: 15px;
      top: 10px;
      width: 24px;
      height: 24px;
      margin-right: 10px;
      outline: 1px solid #d9d9d9;
      border: 1px solid white;
    }
  }

  .actions {
    display: flex;
    justify-content: center;
  }

  .config {
    width: 100%;
    margin-bottom: 0;
    display: flex;
    flex-direction: column;

    .content {
      flex-grow: 1;
    }

    .content-inner {
      overflow-y: auto;
    }
  }

  .color {
    background-color: #f8f8f8;
    border: 1px solid rgb(230, 230, 230);
    padding: 10px;
    border-radius: 4px;
    display: flex;
    flex-wrap: wrap;

    .group {
      flex-grow: 1;
      margin-right: 10px;
    }

    input[type=text],
    input[type=color] {
      margin-bottom: 0;
      flex-grow: 1;
      font-size: 16px;
    }

    &:not(:last-of-type) {
      margin-bottom: 10px;
    }
  }

  input[type=text],
  input[type=number],
  select {
    margin-bottom: 6px;
  }

  .description {
    color: #808080;
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 14px;
    display: flex;
    justify-content: space-between;
  }

  .add {
    fill: white;
    margin-left: 10px;
    padding: 10.5px
  }

  .delete {
    color: var(--primary-color);
    cursor: pointer;
    display: flex;
    align-items: center;

    .svgicon {
      margin-right: 5px;
      fill: #b4b4b4;
    }
  }
</style>

<style lang="scss">
  .section-title {
    font-weight: bold;
    margin-bottom: 15px;
  }

  #onlineOrderingStyle {
    &.mediaOnly {
      .panel>.title {
        border-bottom: none;
        font-size: 16px;
        margin-bottom: 0;
      }

      .panel .content-inner {
        padding-top: 0;
      }
    }

    .panel.config {
      padding: 20px;

      .content {
        flex-grow: 1;
        overflow-y: auto;
        padding-right: 10px;
      }

      textarea {
        height: 10em;
        font-size: 14px;
      }
    }
  }

  .openWebApp {
    margin-bottom: 20px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    .grow {
      flex-grow: 1;
    }

    input.search {
      margin-right: 10px;
      margin-bottom: 0;
    }

    a {
      text-decoration: none;
      display: flex;
      align-items: center;
    }

    .svgicon {
      margin-left: 15px;
      fill: white;
      transform: rotate(-90deg);
    }
  }

  label {
    line-height: 1.1;
  }

  .br {
    border-bottom: 1px solid #d9d9d9;
    width: 100%;
    margin: 15px 0 30px 0;
  }

  .btn-save {
    position: fixed;
    right: 30px;
    bottom: 15px;
  }

  .customCss {
    resize: vertical;
    font-size: 12px;
    font-family: monospace;
    height: 10em;
  }
</style>